import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { adminCmsAPI } from '../../lib/cmsApi';
import {
  FileText,
  Briefcase,
  GraduationCap,
  Users,
  FolderOpen,
  Settings,
  Image,
  Handshake,
  ArrowRight,
  Loader2,
  LayoutDashboard,
} from 'lucide-react';

const CmsDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pages: 0,
    services: 0,
    formations: 0,
    team: 0,
    projects: 0,
    partners: 0,
    media: 0,
  });

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }

    const fetchStats = async () => {
      try {
        const [pages, services, formations, team, projects, partners, media] = await Promise.all([
          adminCmsAPI.listPages(),
          adminCmsAPI.listServices(),
          adminCmsAPI.listFormations(),
          adminCmsAPI.listTeam(),
          adminCmsAPI.listProjects(),
          adminCmsAPI.listPartners(),
          adminCmsAPI.listMedia(),
        ]);
        
        setStats({
          pages: pages.data?.length || 0,
          services: services.data?.length || 0,
          formations: formations.data?.length || 0,
          team: team.data?.length || 0,
          projects: projects.data?.length || 0,
          partners: partners.data?.length || 0,
          media: media.data?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching CMS stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, navigate]);

  const menuItems = [
    {
      title: 'Pages',
      description: 'Gérer le contenu des pages du site',
      icon: FileText,
      count: stats.pages,
      href: '/cms/pages',
      color: 'bg-blue-500',
    },
    {
      title: 'Expertises',
      description: 'Services et domaines d\'expertise',
      icon: Briefcase,
      count: stats.services,
      href: '/cms/services',
      color: 'bg-green-500',
    },
    {
      title: 'Formations',
      description: 'Programmes de formation',
      icon: GraduationCap,
      count: stats.formations,
      href: '/cms/formations',
      color: 'bg-purple-500',
    },
    {
      title: 'Équipe',
      description: 'Membres de l\'équipe',
      icon: Users,
      count: stats.team,
      href: '/cms/team',
      color: 'bg-orange-500',
    },
    {
      title: 'Projets',
      description: 'Réalisations et projets publics',
      icon: FolderOpen,
      count: stats.projects,
      href: '/cms/projects',
      color: 'bg-teal-500',
    },
    {
      title: 'Partenaires',
      description: 'Collaborateurs et partenaires',
      icon: Handshake,
      count: stats.partners,
      href: '/cms/partners',
      color: 'bg-pink-500',
    },
    {
      title: 'Médias',
      description: 'Images et fichiers',
      icon: Image,
      count: stats.media,
      href: '/cms/media',
      color: 'bg-indigo-500',
    },
    {
      title: 'Paramètres',
      description: 'Configuration du site',
      icon: Settings,
      count: null,
      href: '/cms/settings',
      color: 'bg-gray-500',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-dashboard">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
              >
                <LayoutDashboard className="w-6 h-6 text-[#1D1D1B]" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">Gestion du contenu</h1>
                <p className="text-white/60 text-sm">Administration du site web PEY Consultants</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/" target="_blank">
                <Button variant="outline-light" size="sm">
                  Voir le site
                </Button>
              </Link>
              <Link to="/portail">
                <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                  Retour au portail
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1D1D1B]">{stats.pages}</p>
              <p className="text-sm text-neutral-500">Pages</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1D1D1B]">{stats.services}</p>
              <p className="text-sm text-neutral-500">Expertises</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1D1D1B]">{stats.formations}</p>
              <p className="text-sm text-neutral-500">Formations</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#1D1D1B]">{stats.media}</p>
              <p className="text-sm text-neutral-500">Médias</p>
            </CardContent>
          </Card>
        </div>

        {/* Menu Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {menuItems.map((item) => (
            <Link key={item.href} to={item.href}>
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    {item.count !== null && (
                      <span className="text-2xl font-bold text-[#1D1D1B]">{item.count}</span>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[#1D1D1B] mb-1">{item.title}</h3>
                  <p className="text-neutral-500 text-sm mb-4">{item.description}</p>
                  <div className="flex items-center text-[#F9B438] text-sm font-medium group-hover:gap-2 transition-all">
                    Gérer <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Help Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Aide rapide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-[#1D1D1B] mb-2">Modifier une page</h4>
                <p className="text-neutral-600">
                  Allez dans "Pages", sélectionnez la page à modifier, puis éditez les sections une par une.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-[#1D1D1B] mb-2">Ajouter un service</h4>
                <p className="text-neutral-600">
                  Allez dans "Expertises" et cliquez sur "Nouveau service". Remplissez le formulaire et publiez.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-[#1D1D1B] mb-2">Téléverser une image</h4>
                <p className="text-neutral-600">
                  Utilisez la section "Médias" pour téléverser et organiser vos images et fichiers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CmsDashboard;
